import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import AppShell from "../../components/layout/AppShell";
import { PaymentCell } from "../../components/payments/PaymentInfo";
import { RevenueSummary } from "../../components/payments/RevenueSummary";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import {
  Plus, Warning, Clock, CheckCircle, XCircle, Eye, Trash,
  CaretLeft, CaretRight, CalendarBlank, MapPin, User, Phone,
  Path, Timer, ThermometerSimple, X as XIcon, UserPlus, Calendar, Bell,
} from "@phosphor-icons/react";
import { useAuth } from "../../lib/auth";

const STATE_STYLE = {
  NEW: "bg-[#F9E4B7] text-[#7A5A11]",
  QUOTED: "bg-[#D4E0D1] text-[#1A3317]",
  APPROVED: "bg-[#C8E6C9] text-[#1B5E20]",
  REJECTED: "bg-[#F4C7B9] text-[#7A2E18]",
  SCHEDULED: "bg-[#BBDEFB] text-[#0D47A1]",
  IN_PROGRESS: "bg-[#F9E4B7] text-[#7A5A11]",
  COMPLETED: "bg-[#2D5A27] text-white",
  CANCELLED: "bg-[#E5E2D9] text-[#5C5A55]",
};
const PRIORITY_STYLE = {
  LOW: "bg-[#E5E2D9] text-[#5C5A55]",
  NORMAL: "bg-[#D4E0D1] text-[#1A3317]",
  HIGH: "bg-[#F9E4B7] text-[#7A5A11]",
  URGENT: "bg-[#F4C7B9] text-[#7A2E18]",
};

function fmtDue(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = d - new Date();
  const h = Math.floor(Math.abs(diff) / 3_600_000);
  const m = Math.floor((Math.abs(diff) % 3_600_000) / 60_000);
  return (diff < 0 ? "−" : "") + `${h}h ${m}m`;
}

// ---------- Week-strip helpers ----------
const WEEK_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function startOfWeek(d) {
  // ISO week (Mon-first). Returns a fresh Date set to 00:00:00 local.
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const dow = (x.getDay() + 6) % 7;  // 0 = Mon
  x.setDate(x.getDate() - dow);
  return x;
}
function isoDate(d) {
  const x = new Date(d);
  // Use local-date components so calendar matches the operator's wall clock,
  // not a UTC drift that would push 12:30am IST entries onto the wrong day.
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
}
function shiftDays(d, n) {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}
function rangeLabel(start, end) {
  const sameMonth = start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear();
  const sameYear = start.getFullYear() === end.getFullYear();
  if (sameMonth) {
    return `${MONTHS[start.getMonth()]} ${start.getDate()} – ${end.getDate()}, ${end.getFullYear()}`;
  }
  if (sameYear) {
    return `${MONTHS[start.getMonth()]} ${start.getDate()} – ${MONTHS[end.getMonth()]} ${end.getDate()}, ${end.getFullYear()}`;
  }
  return `${MONTHS[start.getMonth()]} ${start.getDate()}, ${start.getFullYear()} – ${MONTHS[end.getMonth()]} ${end.getDate()}, ${end.getFullYear()}`;
}

export default function ServiceRequestList() {
  const { user } = useAuth();
  const canDelete = user?.role === "superadmin" || user?.role === "admin";
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");
  const [overdue, setOverdue] = useState(false);
  const [payFilter, setPayFilter] = useState("");
  // Iter 205 · Pagination — client-side, 25 per page. Ops rarely needs
  // deep archives so a slice-based approach beats server-side keyset
  // pagination for the load-everything endpoint we already have.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  // Iter 204 · Customer search + filter tabs
  const [customerQ, setCustomerQ] = useState("");
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerBucket, setCustomerBucket] = useState("all");  // all | pending | scheduled | completed
  // Week strip state
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [weekItems, setWeekItems] = useState([]);
  const [weekLoading, setWeekLoading] = useState(false);
  // Iter 161 — day-detail modal opened by "+ N more"
  const [dayModal, setDayModal] = useState(null);
  const nav = useNavigate();

  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => shiftDays(weekStart, i));
  }, [weekStart]);
  const weekEnd = weekDays[6];
  const todayIso = isoDate(new Date());

  const itemsByDay = useMemo(() => {
    const map = {};
    for (const it of weekItems) {
      // Prefer the actually-scheduled date; fall back to preferred when ops
      // hasn't yet locked a slot. Skip rows without any date attached.
      const key = it.scheduled_date || it.preferred_date;
      if (!key) continue;
      (map[key] ||= []).push(it);
    }
    return map;
  }, [weekItems]);

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (status) params.set("status", status);
      if (priority) params.set("priority", priority);
      if (overdue) params.set("overdue", "true");
      if (payFilter) params.set("payment", payFilter);
      if (selectedCustomer?.id) params.set("customer_id", selectedCustomer.id);
      const [r, s] = await Promise.all([
        api.get(`/service-requests?${params}`),
        api.get("/service-requests-stats/summary"),
      ]);
      setItems(r.data);
      setStats(s.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, priority, overdue, selectedCustomer, payFilter]);

  // Iter 204 · Debounced customer typeahead — only fires while there is
  // no customer selected. Once a customer is picked we clamp the SR
  // list to their bookings until the user clears the chip.
  useEffect(() => {
    if (selectedCustomer) return;
    const q = customerQ.trim();
    if (q.length < 2) { setCustomerSuggestions([]); return; }
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/customers?q=${encodeURIComponent(q)}&sort_by=name`);
        if (alive) setCustomerSuggestions((r.data?.items || r.data || []).slice(0, 8));
      } catch {}
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [customerQ, selectedCustomer]);

  // Iter 204 · Bucket-filter items in the browser once we already
  // loaded them for a specific customer — avoids a second round-trip
  // when the ops user just wants to toggle All / Pending / Scheduled /
  // Completed for the same customer.
  const bucketedItems = useMemo(() => {
    if (!selectedCustomer) return items;
    if (customerBucket === "all") return items;
    if (customerBucket === "pending") {
      return items.filter(r => ["NEW", "QUOTED", "APPROVED"].includes(r.status));
    }
    if (customerBucket === "scheduled") {
      return items.filter(r => ["SCHEDULED", "IN_PROGRESS"].includes(r.status));
    }
    if (customerBucket === "completed") {
      return items.filter(r => r.status === "COMPLETED");
    }
    return items;
  }, [items, customerBucket, selectedCustomer]);

  const bucketCounts = useMemo(() => ({
    all: items.length,
    pending: items.filter(r => ["NEW", "QUOTED", "APPROVED"].includes(r.status)).length,
    scheduled: items.filter(r => ["SCHEDULED", "IN_PROGRESS"].includes(r.status)).length,
    completed: items.filter(r => r.status === "COMPLETED").length,
  }), [items]);

  // Iter 205 · reset to page 1 whenever the filter set changes, and
  // clamp `page` so a deep page can't be "orphaned" after a filter
  // shrinks the list.
  useEffect(() => { setPage(1); }, [status, priority, overdue, selectedCustomer, customerBucket, payFilter]);
  const totalPages = Math.max(1, Math.ceil(bucketedItems.length / pageSize));
  const clampedPage = Math.min(page, totalPages);
  const pagedItems = useMemo(() => {
    const start = (clampedPage - 1) * pageSize;
    return bucketedItems.slice(start, start + pageSize);
  }, [bucketedItems, clampedPage, pageSize]);

  const delRequest = async (r) => {
    const reason = window.prompt(
      `Soft-delete service request ${r.request_no || r.id}?\n\n` +
      "The request will be hidden from lists but history + linked tasks are preserved.\n\n" +
      "Please enter a reason (minimum 3 chars):",
      "",
    );
    if (!reason) return;
    if (reason.trim().length < 3) {
      toast.error("Reason must be at least 3 characters");
      return;
    }
    try {
      await api.delete(`/service-requests/${r.id}`, {
        data: { reason: reason.trim() },
      });
      toast.success("Service request deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const loadWeek = async () => {
    setWeekLoading(true);
    try {
      const from = isoDate(weekStart);
      const to = isoDate(weekEnd);
      const r = await api.get(
        `/service-requests?from_date=${from}&to_date=${to}`,
      );
      setWeekItems(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    setWeekLoading(false);
  };
  useEffect(() => { loadWeek(); /* eslint-disable-next-line */ }, [weekStart]);

  return (
    <AppShell title="Service Requests" subtitle="Operations · Ad-hoc pickup intake"
      actions={
        <button onClick={() => nav("/operations/service-requests/new")} data-testid="btn-new-sr"
                className="inline-flex items-center gap-2 h-10 px-4 bg-[#2D5A27] hover:bg-[#3A7033] text-white rounded-md text-sm font-medium">
          <Plus size={14} weight="bold"/>New request
        </button>
      }>
      <RevenueSummary refreshKey={items.length}/>
      {/* Iter 161 — Customer-created SR inbox (with nearby-agent
          intelligence + weather). Sits above the weekly grid. */}
      <CustomerSrInbox onAssign={loadWeek} onReschedule={loadWeek}/>

      {/* ─────────────── Weekly schedule strip ─────────────── */}
      <section className="bg-white border border-[#E5E2D9] rounded-md mb-5"
                data-testid="sr-week-strip">
        <header className="flex items-center justify-between px-4 py-3 border-b border-[#F0ECE3]">
          <div className="flex items-center gap-2">
            <CalendarBlank size={15} weight="duotone" color="#2D5A27"/>
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A8780] font-semibold">
                Week schedule
              </div>
              <div className="font-heading text-base text-[#1A1A1A]" data-testid="week-range-label">
                {rangeLabel(weekStart, weekEnd)}
                {weekLoading && (
                  <span className="ml-2 text-[10px] text-[#8A8780] font-normal italic">loading…</span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => setWeekStart(startOfWeek(shiftDays(weekStart, -7)))}
                    data-testid="week-prev"
                    className="h-8 w-8 grid place-items-center rounded-md hover:bg-[#F7F6F2] text-[#5C5A55]"
                    title="Previous week">
              <CaretLeft size={14} weight="bold"/>
            </button>
            <button onClick={() => setWeekStart(startOfWeek(new Date()))}
                    data-testid="week-today"
                    className="h-8 px-3 rounded-md hover:bg-[#F7F6F2] text-[#2D5A27] text-xs font-medium">
              This week
            </button>
            <button onClick={() => setWeekStart(startOfWeek(shiftDays(weekStart, 7)))}
                    data-testid="week-next"
                    className="h-8 w-8 grid place-items-center rounded-md hover:bg-[#F7F6F2] text-[#5C5A55]"
                    title="Next week">
              <CaretRight size={14} weight="bold"/>
            </button>
          </div>
        </header>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 divide-x divide-[#F0ECE3] border-t border-[#F0ECE3]">
          {weekDays.map((d, idx) => {
            const key = isoDate(d);
            const list = itemsByDay[key] || [];
            const isToday = key === todayIso;
            const isWeekend = idx >= 5;
            return (
              <div key={key} data-testid={`week-day-${key}`}
                   className={`min-h-[180px] p-3 flex flex-col gap-2 ${
                     isToday ? "bg-[#F0F7EE]" : isWeekend ? "bg-[#FAFAF7]" : "bg-white"
                   }`}>
                <div className="flex items-baseline justify-between">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#8A8780]">
                      {WEEK_LABELS[idx]}
                    </div>
                    <div className={`font-heading text-xl ${isToday ? "text-[#2D5A27]" : "text-[#1A1A1A]"}`}>
                      {d.getDate()} <span className="text-[10px] font-sans uppercase tracking-[0.15em] text-[#8A8780] font-normal">{MONTHS[d.getMonth()]}</span>
                    </div>
                  </div>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${
                    list.length === 0 ? "bg-[#F7F6F2] text-[#8A8780]" :
                    list.length >= 5 ? "bg-[#F4C7B9] text-[#7A2E18]" :
                    "bg-[#D4E0D1] text-[#1A3317]"
                  }`} data-testid={`week-day-count-${key}`}>
                    {list.length}
                  </span>
                </div>
                {list.length === 0 && (
                  <div className="text-[11px] text-[#B5B2AB] italic mt-1">No requests</div>
                )}
                <div className="flex flex-col gap-1.5">
                  {list.slice(0, 6).map((r) => (
                    <Link key={r.id} to={`/operations/service-requests/${r.id}`}
                          data-testid={`week-item-${r.id}`}
                          className="block rounded border border-[#F0ECE3] hover:border-[#2D5A27] hover:bg-white px-2 py-1.5 transition group">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className={`w-1.5 h-1.5 rounded-full flex-none ${
                          r.priority === "URGENT" ? "bg-[#C25D38]" :
                          r.priority === "HIGH" ? "bg-[#D4A017]" :
                          "bg-[#2D5A27]"
                        }`}/>
                        <span className="font-mono text-[10px] text-[#8A8780] truncate">
                          {r.request_no}
                        </span>
                        {r.preferred_slot && (
                          <span className="ml-auto text-[10px] text-[#5C5A55] font-mono whitespace-nowrap">
                            {r.preferred_slot}
                          </span>
                        )}
                      </div>
                      <div className="text-[12px] text-[#1A1A1A] font-medium truncate group-hover:text-[#2D5A27]">
                        {r.customer_name}
                      </div>
                      <div className="flex items-center gap-1 mt-1">
                        <span className={`px-1.5 py-0.5 text-[9px] uppercase tracking-[0.15em] rounded ${STATE_STYLE[r.status]}`}>
                          {r.status}
                        </span>
                        {(r.is_overdue_response || r.is_overdue_completion) && (
                          <Warning size={10} className="text-[#C25D38]" weight="fill"/>
                        )}
                      </div>
                    </Link>
                  ))}
                  {list.length > 6 && (
                    <button onClick={() => setDayModal({ date: key, list })}
                            data-testid={`week-more-${key}`}
                            className="text-[10px] text-[#2D5A27] font-semibold hover:underline text-left px-1">
                      + {list.length - 6} more · view all
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {stats && (
        <div className="grid grid-cols-5 gap-3 mb-5" data-testid="sr-stats">
          <Stat label="Total" value={stats.total}/>
          <Stat label="New" value={stats.by_status?.NEW || 0} color="#7A5A11"/>
          <Stat label="Scheduled" value={stats.by_status?.SCHEDULED || 0} color="#0D47A1"/>
          <Stat label="Completed" value={stats.by_status?.COMPLETED || 0} color="#2D5A27"/>
          <Stat label="Overdue" value={stats.overdue_response + stats.overdue_completion} color="#C25D38"/>
        </div>
      )}

      <div className="flex gap-2 mb-4 items-center flex-wrap" data-testid="sr-filters">
        <select value={status} onChange={e => setStatus(e.target.value)}
                className="h-9 px-3 border border-[#E5E2D9] rounded-md bg-white text-sm">
          <option value="">All statuses</option>
          {["NEW","QUOTED","APPROVED","REJECTED","SCHEDULED","IN_PROGRESS","COMPLETED","CANCELLED"].map(s =>
            <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={priority} onChange={e => setPriority(e.target.value)}
                className="h-9 px-3 border border-[#E5E2D9] rounded-md bg-white text-sm">
          <option value="">All priorities</option>
          {["LOW","NORMAL","HIGH","URGENT"].map(p => <option key={p} value={p}>{p}</option>)}
        </select>
        <select value={payFilter} onChange={e => setPayFilter(e.target.value)} data-testid="filter-payment"
                className="h-9 px-3 border border-[#E5E2D9] rounded-md bg-white text-sm">
          <option value="">All payments</option>
          <option value="prepaid">Prepaid</option>
          <option value="paid">Paid</option>
          <option value="unpaid">Unpaid</option>
        </select>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={overdue} onChange={e => setOverdue(e.target.checked)} data-testid="filter-overdue"/>
          <span>Only overdue</span>
        </label>
      </div>

      {/* Iter 204 · Customer search + bucket filter tabs */}
      <div className="mb-4 relative" data-testid="sr-customer-search-wrap">
        {selectedCustomer ? (
          <div className="flex items-center flex-wrap gap-2 p-3 bg-[#EFF3EE] border border-[#C8E6C9] rounded-md"
                data-testid="sr-selected-customer">
            <User size={14} className="text-[#1B5E20]"/>
            <div className="font-medium text-sm text-[#1A1A1A]">{selectedCustomer.name}</div>
            {selectedCustomer.customer_code && (
              <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-white border border-[#C8E6C9] text-[#1B5E20]">
                {selectedCustomer.customer_code}
              </span>
            )}
            {selectedCustomer.phone && (
              <span className="text-[11px] text-[#5C5A55] flex items-center gap-1">
                <Phone size={11}/>{selectedCustomer.phone}
              </span>
            )}
            <button onClick={() => { setSelectedCustomer(null); setCustomerQ(""); setCustomerBucket("all"); }}
                     data-testid="sr-clear-customer"
                     className="ml-auto text-[11px] text-[#7A2E18] hover:underline inline-flex items-center gap-1">
              <XIcon size={11}/>Clear
            </button>
          </div>
        ) : (
          <div className="relative">
            <input type="text" value={customerQ} onChange={e => setCustomerQ(e.target.value)}
                   data-testid="sr-customer-search"
                   placeholder="Search customer by name, phone or code…"
                   className="w-full h-10 pl-9 pr-3 border border-[#E5E2D9] rounded-md text-sm focus:outline-none focus:border-[#DC2626]"/>
            <User size={14} className="absolute left-3 top-3 text-[#8A8780]"/>
            {customerSuggestions.length > 0 && (
              <div className="absolute z-30 top-11 left-0 right-0 bg-white border border-[#E5E2D9] rounded-md shadow-lg max-h-72 overflow-y-auto"
                    data-testid="sr-customer-suggestions">
                {customerSuggestions.map(c => (
                  <button key={c.id}
                          onClick={() => { setSelectedCustomer(c); setCustomerQ(""); setCustomerSuggestions([]); }}
                          data-testid={`sr-cust-opt-${c.id}`}
                          className="w-full text-left px-3 py-2 hover:bg-[#F7F6F2] border-b border-[#E5E2D9] last:border-0 flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate">{c.name}</div>
                      <div className="text-[11px] text-[#5C5A55] flex gap-2">
                        {c.customer_code && <span className="font-mono">{c.customer_code}</span>}
                        {c.phone && <span>{c.phone}</span>}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {selectedCustomer && (
          <div className="mt-3 flex gap-1 flex-wrap" data-testid="sr-customer-buckets">
            {[
              { k: "all", l: "All" },
              { k: "pending", l: "Pending" },
              { k: "scheduled", l: "Scheduled" },
              { k: "completed", l: "Completed" },
            ].map(b => (
              <button key={b.k} onClick={() => setCustomerBucket(b.k)}
                       data-testid={`sr-bucket-${b.k}`}
                       className={`h-8 px-3 rounded-md text-xs font-medium transition inline-flex items-center gap-1.5 ${
                         customerBucket === b.k
                           ? "bg-[#DC2626] text-white"
                           : "bg-white border border-[#E5E2D9] text-[#5C5A55] hover:border-[#DC2626]"}`}>
                {b.l}
                <span className={`text-[10px] px-1 py-0.5 rounded ${customerBucket === b.k ? "bg-white/20" : "bg-[#F7F6F2]"}`}>
                  {bucketCounts[b.k]}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white border border-[#E5E2D9] rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="sr-table">
          <thead>
            <tr className="bg-[#F7F6F2] text-left text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
              <th className="px-4 py-3">No.</th>
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3">Service</th>
              <th className="px-4 py-3">Priority</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Preferred</th>
              <th className="px-4 py-3">Quote</th>
              <th className="px-4 py-3">Payment</th>
              <th className="px-4 py-3">Response SLA</th>
              <th className="px-4 py-3">Complete SLA</th>
              <th className="px-4 py-3 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {bucketedItems.length === 0 && <tr><td colSpan={11} className="px-6 py-12 text-center text-[#8A8780]">No requests.</td></tr>}
            {pagedItems.map((r, i) => (
              <tr key={r.id} className={`${i % 2 === 0 ? "bg-white" : "bg-[#FAFAF7]"} ${(r.is_overdue_response || r.is_overdue_completion) ? "border-l-2 border-[#C25D38]" : ""}`}
                  data-testid={`sr-row-${r.id}`}>
                <td className="px-4 py-3 font-mono text-xs">{r.request_no}</td>
                <td className="px-4 py-3">
                  <div className="font-medium">{r.customer_name}</div>
                  <div className="text-[10px] text-[#8A8780] font-mono">{r.customer_code}</div>
                  {/* Iter 74/77 — declared primary-waste + pickup-schedule chips. */}
                  {((r.primary_waste_names || []).length > 0
                     || (r.customer_auto_schedule_frequency
                          && r.customer_auto_schedule_frequency !== "on_demand")) && (
                    <div className="mt-1 flex flex-wrap gap-1" data-testid={`sr-primary-waste-${r.id}`}>
                      {(r.primary_waste_names || []).slice(0, 3).map((n) => (
                        <span key={n}
                              className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#F9F0D6] border border-[#F5C518]/50 text-[#7A5A11] font-semibold">
                          {n}
                        </span>
                      ))}
                      {(r.primary_waste_names || []).length > 3 && (
                        <span className="text-[9px] text-[#8A8780]">+{r.primary_waste_names.length - 3}</span>
                      )}
                      {r.customer_auto_schedule_frequency && r.customer_auto_schedule_frequency !== "on_demand" && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#DDE9F6] border border-[#BBDEFB] text-[#0D47A1] font-semibold"
                              data-testid={`sr-schedule-${r.id}`}>
                          {({
                            weekly: "Weekly",
                            alt_weekly: "Alt-week",
                            monthly: "Monthly",
                          })[r.customer_auto_schedule_frequency] || r.customer_auto_schedule_frequency} pickup
                        </span>
                      )}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-xs">{r.service_category}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded ${PRIORITY_STYLE[r.priority]}`}>{r.priority}</span>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.18em] rounded ${STATE_STYLE[r.status]}`}>{r.status}</span>
                </td>
                <td className="px-4 py-3 font-mono text-xs">{r.preferred_date}<div className="text-[10px] text-[#8A8780]">{r.preferred_slot || ""}</div></td>
                <td className="px-4 py-3 font-mono text-xs">{r.quote_amount != null ? `₹${r.quote_amount}` : "—"}</td>
                <td className="px-4 py-3"><PaymentCell payment={r.payment} testid={`sr-payment-${r.id}`}/></td>
                <td className="px-4 py-3 font-mono text-xs">
                  {r.status === "NEW" ? (
                    <span className={r.is_overdue_response ? "text-[#C25D38] font-semibold" : "text-[#5C5A55]"}>
                      {r.is_overdue_response ? <Warning size={10} className="inline mr-1"/> : null}
                      {fmtDue(r.response_due_at)}
                    </span>
                  ) : (
                    r.response_minutes != null ? <span className="text-[#2D5A27]"><CheckCircle size={10} className="inline mr-1"/>{r.response_minutes}m</span> : "—"
                  )}
                </td>
                <td className="px-4 py-3 font-mono text-xs">
                  {["APPROVED","SCHEDULED","IN_PROGRESS"].includes(r.status) ? (
                    <span className={r.is_overdue_completion ? "text-[#C25D38] font-semibold" : "text-[#5C5A55]"}>
                      {r.is_overdue_completion ? <Warning size={10} className="inline mr-1"/> : null}
                      {fmtDue(r.completion_due_at)}
                    </span>
                  ) : r.status === "COMPLETED" ? (
                    <span className="text-[#2D5A27]"><CheckCircle size={10} className="inline mr-1"/>done</span>
                  ) : "—"}
                </td>
                <td className="px-4 py-3 text-right">
                  <Link to={`/operations/service-requests/${r.id}`} data-testid={`view-${r.id}`}
                        className="text-[#2D5A27] text-xs inline-flex items-center gap-1"><Eye size={12}/>Open</Link>
                  {canDelete && (
                    <button data-testid={`sr-del-${r.id}`} onClick={() => delRequest(r)}
                            className="text-[#C25D38] text-xs inline-flex items-center gap-1 ml-3">
                      <Trash size={12}/>Delete
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* Iter 205 · Pagination footer */}
      {bucketedItems.length > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-3 justify-between text-xs" data-testid="sr-pagination">
          <div className="text-[#5C5A55]" data-testid="sr-pagination-info">
            Showing <strong>{(clampedPage - 1) * pageSize + 1}</strong>
            –<strong>{Math.min(clampedPage * pageSize, bucketedItems.length)}</strong>
            {" of "}<strong>{bucketedItems.length}</strong>
            {bucketedItems.length !== items.length && (
              <span className="text-[#8A8780]"> (filtered from {items.length})</span>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <label className="text-[#5C5A55]">Rows</label>
            <select value={pageSize}
                     onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
                     data-testid="sr-page-size"
                     className="h-7 px-2 border border-[#E5E2D9] rounded bg-white text-xs">
              {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <button onClick={() => setPage(1)} disabled={clampedPage === 1}
                     data-testid="sr-page-first"
                     className="h-7 px-2 border border-[#E5E2D9] rounded bg-white text-xs disabled:opacity-40 hover:border-[#DC2626]">« First</button>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={clampedPage === 1}
                     data-testid="sr-page-prev"
                     className="h-7 px-2 border border-[#E5E2D9] rounded bg-white text-xs disabled:opacity-40 hover:border-[#DC2626]">‹ Prev</button>
            <span className="px-2 font-mono" data-testid="sr-page-indicator">
              {clampedPage} / {totalPages}
            </span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={clampedPage >= totalPages}
                     data-testid="sr-page-next"
                     className="h-7 px-2 border border-[#E5E2D9] rounded bg-white text-xs disabled:opacity-40 hover:border-[#DC2626]">Next ›</button>
            <button onClick={() => setPage(totalPages)} disabled={clampedPage >= totalPages}
                     data-testid="sr-page-last"
                     className="h-7 px-2 border border-[#E5E2D9] rounded bg-white text-xs disabled:opacity-40 hover:border-[#DC2626]">Last »</button>
          </div>
        </div>
      )}
      {dayModal && (
        <DayScheduleModal date={dayModal.date} list={dayModal.list}
                          onClose={() => setDayModal(null)}/>
      )}
    </AppShell>
  );
}

const Stat = ({ label, value, color }) => (
  <div className="bg-white border border-[#E5E2D9] rounded-md p-4">
    <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8780]">{label}</div>
    <div className="font-heading text-3xl mt-1" style={{ color: color || "#1A1A1A" }}>{value}</div>
  </div>
);

// ============================================================
// Iter 161 · Day-schedule enlargement modal
// Opens when the "+N more" chip on a busy day is clicked. Shows
// the FULL list for that day with a live text filter.
// ============================================================
function DayScheduleModal({ date, list, onClose }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    if (!q.trim()) return list;
    const s = q.toLowerCase();
    return list.filter(r =>
      (r.customer_name || "").toLowerCase().includes(s) ||
      (r.request_no || "").toLowerCase().includes(s) ||
      (r.service_category || "").toLowerCase().includes(s)
    );
  }, [list, q]);
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         data-testid="day-schedule-modal" onClick={onClose}>
      <div className="bg-white rounded-lg border border-[#E5E2D9] w-full max-w-2xl max-h-[80vh] flex flex-col"
           onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E5E2D9]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A8780] font-semibold">
              Day schedule
            </div>
            <div className="font-heading text-lg">{new Date(date).toDateString()}</div>
            <div className="text-[11px] text-[#5C5A55]">
              {filtered.length} of {list.length} request{list.length === 1 ? "" : "s"}
            </div>
          </div>
          <button onClick={onClose} data-testid="day-schedule-close"
                  className="h-9 w-9 rounded-md hover:bg-[#F7F6F2] text-[#5C5A55] flex items-center justify-center">
            <XIcon size={16} weight="bold"/>
          </button>
        </div>
        <div className="px-5 py-2 border-b border-[#E5E2D9]">
          <input value={q} onChange={e => setQ(e.target.value)}
                 placeholder="Filter by customer, SR no, or service…"
                 data-testid="day-schedule-search"
                 className="w-full h-9 px-3 border border-[#E5E2D9] rounded-md text-sm focus:border-[#2D5A27] outline-none"/>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
          {filtered.length === 0 && (
            <div className="text-center text-[#8A8780] py-8 text-sm">No matches.</div>
          )}
          {filtered.map(r => (
            <Link key={r.id} to={`/operations/service-requests/${r.id}`}
                  data-testid={`day-row-${r.id}`}
                  className="block px-3 py-2.5 border border-[#E5E2D9] rounded hover:border-[#2D5A27] hover:bg-[#F0F7EE]">
              <div className="flex items-center gap-2">
                <span className={`w-1.5 h-1.5 rounded-full ${
                  r.priority === "URGENT" ? "bg-[#C25D38]" :
                  r.priority === "HIGH"   ? "bg-[#D4A017]" :
                  "bg-[#2D5A27]"
                }`}/>
                <span className="font-mono text-[10px] text-[#8A8780]">{r.request_no}</span>
                {r.preferred_slot && (
                  <span className="ml-auto text-[10px] text-[#5C5A55] font-mono">{r.preferred_slot}</span>
                )}
              </div>
              <div className="text-sm font-medium mt-0.5">{r.customer_name}</div>
              <div className="text-[11px] text-[#5C5A55] mt-0.5 flex items-center gap-2">
                <span>{r.service_category}</span>
                <span className={`px-1.5 py-0.5 text-[9px] uppercase tracking-[0.14em] rounded ${STATE_STYLE[r.status]}`}>
                  {r.status}
                </span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Iter 161 · Customer-created SR Inbox
// Fetches `/operations/sr-inbox` and renders a day / week / month
// grid of customer-created service requests. Each urgent row has a
// pulsing red border; each row surfaces a mini card of the top
// nearby active agents (with road-distance + ETA), plus the weather
// at the customer's location. "Quick assign" + "Reschedule" buttons
// use the existing /service-requests/{id}/schedule + /reschedule
// endpoints so no extra backend was needed for actions.
// ============================================================
function CustomerSrInbox({ onAssign, onReschedule }) {
  const [view, setView] = useState("week");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [assignFor, setAssignFor] = useState(null);
  const [rescheduleFor, setRescheduleFor] = useState(null);
  // Iter 162 — sort + date filter + audible ping
  const [sortBy, setSortBy] = useState("date");   // date | tier | nature
  const [pickedDate, setPickedDate] = useState("");
  const [audioOn, setAudioOn] = useState(true);
  const knownIdsRef = React.useRef(new Set());
  const audioRef = React.useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = pickedDate
        ? `view=day&from_date=${pickedDate}&to_date=${pickedDate}`
        : `view=${view}`;
      const r = await api.get(`/operations/sr-inbox?${params}`);
      setData(r.data);
      // Iter 162 — chime whenever a NEW customer-made SR arrives
      // between polls. Skip the first load (would ping for every
      // existing row).
      const seen = knownIdsRef.current;
      const fresh = [];
      for (const day of r.data.days || []) {
        for (const req of day.requests || []) {
          if (seen.size > 0 && !seen.has(req.id)) fresh.push(req);
          seen.add(req.id);
        }
      }
      if (fresh.length > 0 && audioOn && audioRef.current) {
        try {
          audioRef.current.currentTime = 0;
          // .play() returns a promise that rejects if the browser hasn't
          // had a user gesture yet. Swallow that quietly — the next
          // user gesture will unlock playback for future chimes.
          const p = audioRef.current.play();
          if (p && typeof p.catch === "function") p.catch(() => {});
        } catch { /* older browsers throw synchronously */ }
        toast.info(`${fresh.length} new customer SR${fresh.length === 1 ? "" : "s"}`);
      }
    } catch (e) { toast.error(formatApiError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [view, pickedDate]);
  useEffect(() => {
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
    /* eslint-disable-next-line */
  }, [view, pickedDate, audioOn]);

  // Iter 175 — Client-side sort. `date` keeps the per-day grouping;
  // `tier` and `nature` FLATTEN every request across the week into a
  // single virtual "day" so the highest-priority row is always at the
  // top (previously the sort only re-ordered within a single day, so
  // if two VVIPs were on different days the user couldn't see them
  // together).
  const sortedDays = useMemo(() => {
    if (!data) return [];
    const tierRank = { vvip: 3, vip: 2, priority: 1, regular: 0 };
    const natureRank = { EMERGENCY: 4, URGENT: 3, HIGH: 2, NORMAL: 1, LOW: 0 };
    if (sortBy === "date") {
      const cmp = (a, b) =>
        (a.preferred_slot || "").localeCompare(b.preferred_slot || "")
        || (a.created_at || "").localeCompare(b.created_at || "");
      return data.days.map(d => ({ ...d, requests: [...d.requests].sort(cmp) }));
    }
    // Flatten every day's requests into one list, then group under a
    // synthetic "Sorted by …" band so the UI still shows a day header.
    const all = data.days.flatMap(d =>
      d.requests.map(r => ({ ...r, _srcDate: d.date })),
    );
    const cmp = sortBy === "tier"
      ? (a, b) =>
          (tierRank[(b.customer_priority_tier || "").toLowerCase()] || 0)
          - (tierRank[(a.customer_priority_tier || "").toLowerCase()] || 0)
          || (a.preferred_date || "").localeCompare(b.preferred_date || "")
      : (a, b) =>
          (natureRank[(b.priority || "").toUpperCase()] || 0)
          - (natureRank[(a.priority || "").toUpperCase()] || 0)
          || (a.preferred_date || "").localeCompare(b.preferred_date || "");
    all.sort(cmp);
    const urgent_count = all.filter(r => r.is_urgent).length;
    return [{
      date: sortBy === "tier" ? "Sorted · VIP priority" : "Sorted · SR nature",
      total_count: all.length,
      urgent_count,
      requests: all,
      _flat: true,
    }];
  }, [data, sortBy]);

  // Iter 185 — quick-assign in the row: post directly to /schedule
  // with today + the SR's preferred slot (or empty).
  const quickAssign = async (sr, agent) => {
    const date = sr.scheduled_date || sr.preferred_date
                 || new Date().toISOString().slice(0, 10);
    try {
      await api.post(`/service-requests/${sr.id}/schedule`, {
        assigned_staff_id: agent.id,
        scheduled_date: date,
        scheduled_slot: sr.scheduled_slot || sr.preferred_slot || null,
        auto_create_task: true,
      });
      toast.success(`Assigned to ${agent.name} (${date})`);
      load();
      onAssign?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <section className="bg-white border border-[#E5E2D9] rounded-md mb-5"
             data-testid="sr-inbox">
      <audio ref={audioRef} src="/sounds/sr-inbox-notify.mp3" preload="auto"
              data-testid="sr-inbox-chime-audio"/>
      <header className="flex items-center justify-between px-4 py-3 border-b border-[#F0ECE3] flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <User size={15} weight="duotone" color="#7A2E18"/>
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A8780] font-semibold">
              Customer-made SR requests
            </div>
            <div className="font-heading text-base text-[#1A1A1A]">
              {data
                ? `${data.total_requests} incoming · ${data.active_agents} agents live · ${data.from_date}${data.from_date !== data.to_date ? " → " + data.to_date : ""}`
                : "Loading…"}
              {loading && <span className="ml-2 text-[10px] text-[#8A8780] font-normal italic">refreshing…</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => setAudioOn(v => !v)}
                  data-testid="sr-inbox-audio-toggle"
                  title={audioOn ? "Turn OFF new-SR chime" : "Turn ON new-SR chime"}
                  className={`h-9 w-9 rounded-md border inline-flex items-center justify-center ${
                    audioOn
                      ? "bg-[#F0F7EE] border-[#B5D5AC] text-[#2D5A27]"
                      : "bg-white border-[#E5E2D9] text-[#8A8780]"
                  }`}>
            {audioOn ? "🔔" : "🔕"}
          </button>
          <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                  data-testid="sr-inbox-sort"
                  className="h-9 px-2 border border-[#E5E2D9] rounded-md text-xs bg-white">
            <option value="date">Sort · By date/slot</option>
            <option value="tier">Sort · VIP priority</option>
            <option value="nature">Sort · SR nature</option>
          </select>
          <input type="date" value={pickedDate}
                 onChange={e => setPickedDate(e.target.value)}
                 data-testid="sr-inbox-date"
                 title="View SR requests for a specific date"
                 className="h-9 px-2 border border-[#E5E2D9] rounded-md text-xs bg-white"/>
          <Link to="/operations/sr-alerts"
                data-testid="sr-inbox-history-link"
                title="Push Alert History — see cascades and response times"
                className="h-9 px-2.5 rounded-md border border-[#E5E2D9] text-[11px] font-medium text-[#5C5A55] hover:bg-[#F7F6F2] inline-flex items-center gap-1">
            <Bell size={12}/>History
          </Link>
          {pickedDate && (
            <button onClick={() => setPickedDate("")}
                    data-testid="sr-inbox-date-clear"
                    className="h-9 px-2 rounded-md text-xs text-[#7A2E18] hover:bg-[#FBEBE5]">
              clear
            </button>
          )}
          {!pickedDate && (
            <div className="inline-flex rounded-md border border-[#E5E2D9] overflow-hidden">
              {["day", "week", "month"].map(v => (
                <button key={v} onClick={() => setView(v)}
                        data-testid={`sr-inbox-view-${v}`}
                        className={`px-3 h-9 text-xs font-medium capitalize ${
                          view === v
                            ? "bg-[#7A2E18] text-white"
                            : "bg-white text-[#5C5A55] hover:bg-[#F7F6F2]"
                        }`}>
                  {v}
                </button>
              ))}
            </div>
          )}
        </div>
      </header>
      <div className="p-3">
        {/* Iter 184 — Global live-agents radar. Sits above the day list
             so ops can search / dispatch without opening an SR. */}
        <LiveAgentsRadar/>
        {!data && loading && <div className="text-sm text-[#8A8780] italic p-6 text-center">Loading customer requests…</div>}
        {data && data.total_requests === 0 && (
          <div className="text-sm text-[#8A8780] italic p-6 text-center">
            No customer-created service requests in this window.
          </div>
        )}
        {data && data.total_requests > 0 && (
          <div className="space-y-3">
            {sortedDays.filter(d => d.total_count > 0).map(day => (
              <SrInboxDay key={day.date} day={day}
                          onAssign={setAssignFor}
                          onQuickAssign={quickAssign}
                          onReschedule={setRescheduleFor}/>
            ))}
          </div>
        )}
      </div>

      {assignFor && (
        <SrAssignModal sr={assignFor}
                       onClose={() => setAssignFor(null)}
                       onDone={() => { setAssignFor(null); load(); onAssign?.(); }}/>
      )}
      {rescheduleFor && (
        <SrRescheduleModal sr={rescheduleFor}
                           onClose={() => setRescheduleFor(null)}
                           onDone={() => { setRescheduleFor(null); load(); onReschedule?.(); }}/>
      )}
    </section>
  );
}

function SrInboxDay({ day, onAssign, onReschedule, onQuickAssign }) {
  const isFlat = !!day._flat;
  return (
    <div className="rounded border border-[#F0ECE3] overflow-hidden"
         data-testid={`sr-inbox-day-${day.date}`}>
      <div className="flex items-center gap-2 px-3 py-1.5 bg-[#F7F6F2] border-b border-[#F0ECE3]">
        <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#5C5A55]">
          {isFlat ? day.date : new Date(day.date).toDateString()}
        </div>
        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-white border border-[#E5E2D9] text-[#5C5A55]">
          {day.total_count}
        </span>
        {day.urgent_count > 0 && (
          <span className="text-[10px] font-semibold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded bg-[#C25D38] text-white sr-urgent-pill">
            {day.urgent_count} URGENT
          </span>
        )}
      </div>
      <div className="divide-y divide-[#F0ECE3]">
        {day.requests.map(r => (
          <SrInboxRow key={r.id} r={r} showDate={isFlat}
                      onAssign={onAssign} onReschedule={onReschedule}
                      onQuickAssign={onQuickAssign}/>
        ))}
      </div>
    </div>
  );
}

function LiveAgentsRadar() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/operations/live-agents?since_hours=6");
      setData(r.data);
    } catch (e) { /* silent — this is background */ }
    setLoading(false);
  }, []);

  // Poll every 30s while the panel is open.
  useEffect(() => {
    load();
    if (!open) return;
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [open, load]);

  const agents = data?.agents || [];
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return agents;
    return agents.filter(a =>
      (a.name || "").toLowerCase().includes(s)
      || (a.phone || "").toLowerCase().includes(s)
      || (a.duty_status || "").toLowerCase().includes(s));
  }, [agents, q]);

  const freshest = agents[0]?.minutes_ago;
  // Iter 185 — Leaflet mini-map center + zoom based on agent spread.
  const mapCenter = useMemo(() => {
    if (agents.length === 0) return [10.85, 76.27];
    const lats = agents.map(a => a.lat).filter(v => Number.isFinite(v));
    const lngs = agents.map(a => a.lng).filter(v => Number.isFinite(v));
    if (lats.length === 0) return [10.85, 76.27];
    return [
      (Math.min(...lats) + Math.max(...lats)) / 2,
      (Math.min(...lngs) + Math.max(...lngs)) / 2,
    ];
  }, [agents]);
  const dutyChip = (a) => {
    const s = (a.duty_status || "unknown").toLowerCase();
    if (s === "active" || s === "on_duty" || s === "on_task")
      return { text: "On task", cls: "bg-[#DCEEDC] text-[#1A3317]" };
    if (s === "break" || s === "on_break")
      return { text: `On break${a.break_minutes ? ` · ${a.break_minutes} min` : ""}`,
                cls: "bg-[#F9E4B7] text-[#7A5A11]" };
    if (s === "idle") return { text: "Free", cls: "bg-[#E6E6E6] text-[#5C5A55]" };
    if (s === "off_duty" || s === "off")
      return { text: "Off duty", cls: "bg-[#F4C7B9] text-[#7A2E18]" };
    return { text: s.toUpperCase(), cls: "bg-[#F7F6F2] text-[#8A8780]" };
  };
  return (
    <div className="mb-3 rounded-md border border-[#E5E2D9] bg-gradient-to-r from-[#F7F6F2] to-white overflow-hidden"
         data-testid="live-agents-radar">
      <button type="button" onClick={() => setOpen(v => !v)}
              data-testid="live-agents-toggle"
              className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[#F0ECE3]">
        <span className="relative flex h-2 w-2">
          <span className={`absolute inline-flex h-full w-full rounded-full opacity-75 ${agents.length ? "bg-[#2D5A27] animate-ping" : "bg-[#8A8780]"}`}/>
          <span className={`relative inline-flex rounded-full h-2 w-2 ${agents.length ? "bg-[#2D5A27]" : "bg-[#8A8780]"}`}/>
        </span>
        <div className="text-[10px] uppercase tracking-[0.14em] text-[#8A8780] font-semibold">
          Live agents radar
        </div>
        <div className="text-[12px] text-[#1A1A1A] font-semibold">
          {data ? `${agents.length} live` : "…"}
          {freshest != null && (
            <span className="ml-1 text-[10px] text-[#8A8780] font-normal">
              · fresh {freshest} min ago
            </span>
          )}
        </div>
        {loading && <span className="text-[10px] italic text-[#8A8780]">refreshing…</span>}
        <span className="ml-auto text-[11px] text-[#5C5A55]">{open ? "▾" : "▸"}</span>
      </button>
      {open && (
        <div className="border-t border-[#E5E2D9] p-3 space-y-3">
          {/* Iter 185 — mini-map so ops can see agent clusters at a glance. */}
          {agents.length > 0 && (
            <div className="h-56 rounded overflow-hidden border border-[#F0ECE3]"
                  data-testid="live-agents-map">
              <MapContainer center={mapCenter} zoom={11} style={{ height: "100%", width: "100%" }}
                             scrollWheelZoom={false}>
                <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                            attribution='&copy; OpenStreetMap'/>
                {agents.filter(a => Number.isFinite(a.lat) && Number.isFinite(a.lng)).map(a => {
                  const fresh = (a.minutes_ago ?? 999) < 15;
                  const stale = (a.minutes_ago ?? 999) >= 60;
                  const color = fresh ? "#2D5A27" : stale ? "#8A8780" : "#D4A017";
                  return (
                    <CircleMarker key={a.id} center={[a.lat, a.lng]}
                                   radius={7} pathOptions={{ color, fillColor: color, fillOpacity: 0.75 }}>
                      <Popup>
                        <div className="text-[12px]">
                          <div className="font-semibold">{a.name}</div>
                          {a.phone && <div className="font-mono text-[10px]">{a.phone}</div>}
                          <div className="text-[10px] text-gray-500">
                            {a.minutes_ago != null ? `${a.minutes_ago} min ago` : "—"} · {dutyChip(a).text}
                          </div>
                        </div>
                      </Popup>
                    </CircleMarker>
                  );
                })}
              </MapContainer>
            </div>
          )}
          <input value={q} onChange={e => setQ(e.target.value)}
                 data-testid="live-agents-search"
                 placeholder="Search all live staff by name / phone / duty status…"
                 className="w-full h-8 px-2 border border-[#E5E2D9] rounded text-[12px]
                            focus:outline-none focus:border-[#2D5A27]"/>
          {agents.length === 0 && (
            <div className="text-[12px] italic text-[#8A8780] py-3 text-center">
              No field staff have pinged in the last 6 hours.
            </div>
          )}
          {agents.length > 0 && filtered.length === 0 && (
            <div className="text-[12px] italic text-[#8A8780] py-3 text-center">
              No live staff matches "{q}".
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 max-h-72 overflow-y-auto">
            {filtered.map(a => {
              const chip = dutyChip(a);
              return (
                <a key={a.id}
                   href={`https://www.google.com/maps/search/?api=1&query=${a.lat},${a.lng}`}
                   target="_blank" rel="noopener noreferrer"
                   data-testid={`live-agent-${a.id}`}
                   className="flex items-start gap-2 px-2 py-1.5 rounded border border-[#F0ECE3] bg-white hover:border-[#2D5A27] transition">
                  <div className={`mt-1 w-1.5 h-1.5 rounded-full flex-none ${
                    (a.minutes_ago ?? 999) < 15 ? "bg-[#2D5A27]"
                      : (a.minutes_ago ?? 999) < 60 ? "bg-[#D4A017]"
                      : "bg-[#8A8780]"
                  }`}/>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <span className="font-medium text-[12px] text-[#1A1A1A] truncate">{a.name}</span>
                      <span className={`text-[9px] px-1 py-0.5 rounded font-semibold ${chip.cls}`}
                             data-testid={`live-agent-duty-${a.id}`}>
                        {chip.text}
                      </span>
                    </div>
                    {a.phone && <div className="text-[10px] font-mono text-[#5C5A55]">{a.phone}</div>}
                    <div className="text-[10px] text-[#8A8780]">
                      {a.minutes_ago != null ? `${a.minutes_ago} min ago` : "—"}
                      {" · "}
                      <span className="font-mono">{a.lat?.toFixed(4)}, {a.lng?.toFixed(4)}</span>
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function SrInboxRow({ r, onAssign, onReschedule, onQuickAssign, showDate }) {
  const nav = useNavigate();
  const urgent = r.is_urgent;
  const delayTier = r.delay?.tier || "none";
  const delayHours = r.delay?.hours || 0;
  // Iter 183 — per-row search over the nearby-agents list.
  const [agentQ, setAgentQ] = useState("");
  const filteredAgents = useMemo(() => {
    const list = r.nearby_agents || [];
    const q = agentQ.trim().toLowerCase();
    if (!q) return list;
    return list.filter(a =>
      (a.name || "").toLowerCase().includes(q)
      || (a.phone || "").toLowerCase().includes(q)
      || (a.code || "").toLowerCase().includes(q));
  }, [r.nearby_agents, agentQ]);
  // Precedence: on_premises (pink) → urgent (red pulse) → delay-tier.
  // Only the highest-priority animation renders so the row never
  // becomes a rave.
  const rowAnim = r.is_on_premises ? "sr-onprem-blink border-l-4 border-l-[#DB2777] bg-[#FCE7F3]"
                  : urgent          ? "sr-urgent-blink border-l-4 border-l-[#C25D38] bg-[#FDF3F0]"
                  : delayTier === "red"    ? "sr-delay-red    border-l-4 border-l-[#B42828]"
                  : delayTier === "orange" ? "sr-delay-orange border-l-4 border-l-[#D65A0F]"
                  : delayTier === "amber"  ? "sr-delay-amber  border-l-4 border-l-[#E68A19]"
                  : delayTier === "yellow" ? "sr-delay-yellow border-l-4 border-l-[#D4A017]"
                  : "bg-white";
  const delayLabel = delayHours >= 48 ? `${Math.round(delayHours / 24)}d overdue`
                     : delayHours >= 1  ? `${Math.round(delayHours)}h overdue`
                     : null;
  return (
    <div data-testid={`sr-inbox-row-${r.id}`}
         className={`p-3 grid grid-cols-1 md:grid-cols-12 gap-3 items-start ${rowAnim}`}>
      {/* Left — customer + SR meta */}
      <div className="md:col-span-4 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
          <span className="font-mono text-[10px] text-[#8A8780]">{r.request_no}</span>
          {r.prepaid && (
            <span data-testid={`sr-prepaid-${r.id}`}
                   title={`Prepaid · dispatch state: ${r.gig_dispatch_state || "idle"}`}
                   className="px-1.5 py-0.5 text-[9px] uppercase tracking-wider rounded font-bold bg-[#C7E0C2] text-[#2D5A27] border-l-2 border-[#2D5A27]">
              Prepaid
            </span>
          )}
          {r.gig_dispatch_state === "searching" && (
            <span data-testid={`sr-dispatch-${r.id}`}
                   className="px-1.5 py-0.5 text-[9px] uppercase tracking-wider rounded font-semibold bg-[#F9E4B7] text-[#7A5A11] animate-pulse">
              Finding partner
            </span>
          )}
          {r.gig_dispatch_state === "accepted" && (
            <span className="px-1.5 py-0.5 text-[9px] uppercase tracking-wider rounded font-semibold bg-[#DDD1EF] text-[#4A2E6B]">
              Partner accepted
            </span>
          )}
          {showDate && r.preferred_date && (
            <span className="text-[9px] font-mono px-1.5 py-0.5 rounded bg-[#F7F6F2] border border-[#E5E2D9] text-[#5C5A55]"
                  data-testid="sr-inbox-row-date">
              {new Date(r.preferred_date).toLocaleDateString(undefined, { day: "2-digit", month: "short" })}
            </span>
          )}
          {/* Iter 184 — priority pill: blink violet for EMERGENCY,
                blink purple for HIGH, red for other URGENT-tier priorities. */}
          {(() => {
            const p = (r.priority || "NORMAL").toUpperCase();
            const isEmergency = p === "EMERGENCY";
            const isHigh = p === "HIGH";
            const isUrgent = urgent && !isEmergency && !isHigh;
            const cls = isEmergency
              ? "bg-[#6D28D9] text-white animate-pulse shadow-[0_0_0_2px_rgba(109,40,217,0.35)]"
              : isHigh
                ? "bg-[#A855F7] text-white animate-pulse shadow-[0_0_0_2px_rgba(168,85,247,0.30)]"
                : isUrgent
                  ? "bg-[#C25D38] text-white"
                  : "bg-[#E5E2D9] text-[#5C5A55]";
            return (
              <button type="button"
                       data-testid={`sr-priority-${r.id}`}
                       title={`${p} priority — click to open SR ${r.request_no}`}
                       onClick={() => nav(`/service-requests/${r.id}`)}
                       className={`px-2 py-0.5 text-[10px] uppercase tracking-[0.14em] rounded font-bold cursor-pointer hover:brightness-110 focus:outline-none ${cls}`}>
                {p}
              </button>
            );
          })()}
          {r.is_undated && (
            <span data-testid={`sr-undated-${r.id}`}
                  title="Customer submitted this SR without a preferred date — please schedule it"
                  className="px-1.5 py-0.5 text-[9px] uppercase tracking-[0.14em] rounded-full font-bold bg-[#F59E0B] text-[#3B2A00] animate-pulse">
              Needs scheduling
            </span>
          )}
          {r.customer_priority_tier && r.customer_priority_tier !== "regular" && (
            <span className={`px-1.5 py-0.5 text-[9px] uppercase font-bold rounded-full ${
              r.customer_priority_tier === "vvip"
                ? "bg-gradient-to-r from-[#FFF3C4] via-[#FFD86E] to-[#F5B400] text-[#5A4600] border border-[#E0AC00]"
                : "bg-gradient-to-r from-[#EEE0F5] to-[#D8B7E8] text-[#4A1976] border border-[#B589D6]"
            }`}>
              ♛ {r.customer_priority_tier.toUpperCase()}
            </span>
          )}
          {r.is_on_premises && (
            <span data-testid={`sr-onprem-${r.id}`}
                  className="px-1.5 py-0.5 text-[9px] uppercase tracking-[0.14em] rounded-full font-bold bg-[#DB2777] text-white">
              On-premises
            </span>
          )}
          {delayLabel && (
            <span data-testid={`sr-delay-${r.id}`}
                  className={`px-1.5 py-0.5 text-[9px] uppercase tracking-[0.14em] rounded-full font-bold ${
                    delayTier === "red"    ? "bg-[#B42828] text-white" :
                    delayTier === "orange" ? "bg-[#D65A0F] text-white" :
                    delayTier === "amber"  ? "bg-[#E68A19] text-white" :
                    "bg-[#D4A017] text-white"
                  }`}>
              {delayLabel}
            </span>
          )}
        </div>
        <Link to={`/operations/service-requests/${r.id}`}
              data-testid={`sr-inbox-view-${r.id}`}
              className="text-sm font-medium text-[#1A1A1A] hover:text-[#2D5A27] block truncate">
          {r.customer_name || "—"}
        </Link>
        <div className="text-[11px] text-[#5C5A55] flex items-center gap-2 mt-0.5 flex-wrap">
          {r.customer_phone && <span className="inline-flex items-center gap-0.5"><Phone size={10}/>{r.customer_phone}</span>}
          {r.customer_code && <span className="font-mono">{r.customer_code}</span>}
        </div>
        <div className="text-[11px] text-[#5C5A55] mt-0.5">
          {r.service_category || "—"}
          {r.preferred_slot && <> · {r.preferred_slot}</>}
        </div>
        <div className="text-[10px] text-[#8A8780] mt-0.5">
          Status: <strong className="text-[#1A1A1A]">{r.status}</strong>
          {r.assigned_staff_name && <> · Assigned: <strong>{r.assigned_staff_name}</strong></>}
        </div>
        <div className="mt-1.5 p-1.5 rounded-md bg-[#FAFAF7] border border-[#E5E2D9] inline-block">
          <PaymentCell payment={r.payment} testid={`sr-inbox-payment-${r.id}`}/>
        </div>
        {/* Iter 183/220h — Customer's fixed assignment (Ops · Assign Staff)
            + per-SR supervising office staff. */}
        {(r.responsible_field_staff_name || r.responsible_office_staff_name || r.supervising_office_staff_name) && (
          <div className="text-[10px] text-[#5C5A55] mt-1 flex flex-wrap gap-x-2 gap-y-0.5"
                data-testid={`sr-inbox-assignment-${r.id}`}>
            {r.responsible_field_staff_name && (
              <span className="inline-flex items-center gap-1">
                <span className="text-[9px] uppercase tracking-[0.14em] text-[#8A8780]">Field</span>
                <strong className="text-[#1A3317]">{r.responsible_field_staff_name}</strong>
              </span>
            )}
            {r.responsible_office_staff_name && (
              <span className="inline-flex items-center gap-1">
                <span className="text-[9px] uppercase tracking-[0.14em] text-[#8A8780]">Office</span>
                <strong className="text-[#0B2B66]">{r.responsible_office_staff_name}</strong>
              </span>
            )}
            {r.supervising_office_staff_name && r.supervising_office_staff_id !== r.responsible_office_staff_id && (
              <span className="inline-flex items-center gap-1"
                    data-testid={`sr-supervisor-${r.id}`}>
                <span className="text-[9px] uppercase tracking-[0.14em] text-[#8A8780]">Supervisor</span>
                <strong className="text-[#7A2E18]">{r.supervising_office_staff_name}</strong>
              </span>
            )}
          </div>
        )}
        {r.description && (
          <div className="text-[11px] text-[#8A8780] italic mt-1 line-clamp-2">
            &ldquo;{r.description}&rdquo;
          </div>
        )}
      </div>

      {/* Middle — nearby agents */}
      <div className="md:col-span-5">
        <div className="text-[9px] uppercase tracking-[0.14em] text-[#8A8780] font-semibold mb-1 flex items-center gap-1 flex-wrap">
          <MapPin size={10}/> Nearby active agents
          {r.customer_location && r.nearby_agents.length > 0 && (
            <span className="text-[#5C5A55] normal-case tracking-normal font-normal">
              · {r.nearby_agents.length} live
            </span>
          )}
          {!r.customer_location && (
            <span className="text-[#C25D38] normal-case tracking-normal font-normal">
              · customer has no saved GPS
            </span>
          )}
        </div>
        {/* Iter 183 — search across live nearby staff */}
        {r.customer_location && r.nearby_agents.length > 0 && (
          <input value={agentQ} onChange={e => setAgentQ(e.target.value)}
                 data-testid={`sr-nearby-search-${r.id}`}
                 placeholder="Search live nearby staff by name / phone…"
                 className="w-full h-7 px-2 mb-1.5 border border-[#E5E2D9] rounded text-[11px]
                            focus:outline-none focus:border-[#2D5A27]"/>
        )}
        {r.customer_location && r.nearby_agents.length === 0 && (
          <div className="text-[11px] text-[#8A8780] italic">
            No active agent within 10 km.
          </div>
        )}
        {r.customer_location && r.nearby_agents.length > 0
          && filteredAgents.length === 0 && (
          <div className="text-[11px] text-[#8A8780] italic">
            No live staff matches “{agentQ}”.
          </div>
        )}
        <div className="space-y-1">
          {(agentQ ? filteredAgents : filteredAgents.slice(0, 3)).map(a => {
            const dutyText = a.duty_status === "on_task" ? "On task"
              : a.duty_status === "on_break"
                ? `On break${a.break_minutes ? ` · ${a.break_minutes} min` : ""}`
                : a.duty_status === "off_duty" ? "Off duty"
                : a.duty_status === "idle" ? "Free" : null;
            const dutyCls = a.duty_status === "on_task" ? "bg-[#DCEEDC] text-[#1A3317]"
              : a.duty_status === "on_break" ? "bg-[#F9E4B7] text-[#7A5A11]"
              : a.duty_status === "off_duty" ? "bg-[#F4C7B9] text-[#7A2E18]"
              : "bg-[#E6E6E6] text-[#5C5A55]";
            return (
              <div key={a.id} data-testid={`sr-agent-${r.id}-${a.id}`}
                   className="flex items-center gap-2 text-[11px] bg-[#F7F6F2] rounded px-2 py-1">
                <div className="w-1.5 h-1.5 rounded-full bg-[#2D5A27]"/>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[#1A1A1A] truncate flex items-center gap-1.5 flex-wrap">
                    <span>{a.name}</span>
                    {a.phone && (
                      <span className="text-[10px] text-[#8A8780] font-mono font-normal">
                        {a.phone}
                      </span>
                    )}
                    {dutyText && (
                      <span data-testid={`sr-agent-duty-${a.id}`}
                             className={`text-[9px] px-1 py-0.5 rounded font-semibold ${dutyCls}`}>
                        {dutyText}
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] text-[#5C5A55] flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-0.5"><Path size={9}/>{a.road_km} km</span>
                    <span className="inline-flex items-center gap-0.5">
                      <Timer size={9}/>
                      {a.eta_traffic_min != null ? Math.round(a.eta_traffic_min) : Math.round(a.eta_min)} min
                    </span>
                    {a.eta_traffic_min != null
                      ? <span className="text-[#7A2E18] font-semibold" title="Live traffic ETA (Google Directions)">live traffic</span>
                      : a.route_source === "google"
                        ? <span className="text-[#2D5A27]">google</span>
                        : a.route_source === "fallback"
                          ? <span className="italic text-[#8A8780]">estimate</span>
                          : <span className="text-[#2D5A27]">road</span>}
                    {/* Iter 185 — quick assign to this specific agent */}
                    <button type="button"
                             onClick={() => onQuickAssign?.(r, a)}
                             disabled={a.duty_status === "off_duty"}
                             data-testid={`sr-quick-assign-${r.id}-${a.id}`}
                             title={a.duty_status === "off_duty" ? "Off duty" : `Quick-assign to ${a.name}`}
                             className="ml-auto text-[10px] font-semibold text-[#2D5A27] hover:underline disabled:text-[#B5B2AB] disabled:no-underline">
                      Assign →
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
          {!agentQ && filteredAgents.length > 3 && (
            <div className="text-[10px] text-[#8A8780] italic pl-1">
              + {filteredAgents.length - 3} more · type to search
            </div>
          )}
        </div>
      </div>

      {/* Right — weather + actions */}
      <div className="md:col-span-3 flex flex-col gap-2">
        {r.weather && (
          <div className="text-[11px] bg-[#DDE7F1] border border-[#B4CDEB] rounded px-2 py-1 text-[#1F3A57] flex items-center gap-2"
               data-testid={`sr-weather-${r.id}`}>
            <ThermometerSimple size={12} weight="fill"/>
            <div>
              <div className="font-semibold">{Math.round(r.weather.temp_c)}°C · {r.weather.condition}</div>
              {r.weather.wind_kph != null && (
                <div className="text-[9px] opacity-70">wind {Math.round(r.weather.wind_kph)} km/h</div>
              )}
            </div>
          </div>
        )}
        <button onClick={() => onAssign(r)} data-testid={`sr-inbox-assign-${r.id}`}
                className="h-8 px-2 rounded bg-[#2D5A27] hover:bg-[#3A7033] text-white text-[11px] font-semibold inline-flex items-center justify-center gap-1">
          <UserPlus size={11} weight="fill"/>
          {r.assigned_staff_name ? "Reassign" : "Schedule"}
        </button>
        <button onClick={() => onReschedule(r)} data-testid={`sr-inbox-resch-${r.id}`}
                className="h-8 px-2 rounded border border-[#0B2B66] text-[#0B2B66] hover:bg-[#E4EAF5] text-[11px] font-semibold inline-flex items-center justify-center gap-1">
          <Calendar size={11} weight="fill"/>Re-schedule
        </button>
        <Link to={`/operations/service-requests/${r.id}`}
              data-testid={`sr-inbox-open-${r.id}`}
              className="h-8 px-2 rounded border border-[#E5E2D9] text-[#5C5A55] hover:bg-[#F7F6F2] text-[11px] font-medium inline-flex items-center justify-center gap-1">
          <Eye size={11}/>View SR
        </Link>
      </div>
    </div>
  );
}

function SrAssignModal({ sr, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [selectedId, setSelectedId] = useState(sr.nearby_agents[0]?.id || "");
  const [slot, setSlot] = useState(sr.scheduled_slot || sr.preferred_slot || "");
  const [date, setDate] = useState(sr.scheduled_date || sr.preferred_date || "");
  const [vehicleId, setVehicleId] = useState("");
  const [autoTask, setAutoTask] = useState(true);
  // Iter 162 — full-form scheduling: fetch every field staff + fleet
  // so ops can pick anyone. Nearby staff still headline the list.
  const [allStaff, setAllStaff] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [staffQ, setStaffQ] = useState("");
  // Iter 220h — office staff supervisor for this SR (pre-fills with the
  // customer's Fixed Responsibility office staff so the common case is
  // one click).
  const [officeUsers, setOfficeUsers] = useState([]);
  const [supervisorId, setSupervisorId] = useState(
    sr.supervising_office_staff_id || sr.responsible_office_staff_id || ""
  );

  useEffect(() => {
    Promise.all([
      api.get("/staff").catch(() => ({ data: [] })),
      api.get("/vehicles").catch(() => ({ data: [] })),
      api.get("/office-staff").catch(() => ({ data: [] })),
    ]).then(([s, v, o]) => {
      setAllStaff(Array.isArray(s.data) ? s.data : (s.data?.items || []));
      setVehicles(Array.isArray(v.data) ? v.data : (v.data?.items || []));
      setOfficeUsers(Array.isArray(o.data) ? o.data : (o.data?.items || []));
    });
  }, []);

  const nearbyIds = useMemo(
    () => new Set(sr.nearby_agents.map(a => a.id)),
    [sr.nearby_agents]
  );
  const otherStaff = useMemo(() => {
    const q = staffQ.trim().toLowerCase();
    return allStaff
      .filter(s => !nearbyIds.has(s.id))
      .filter(s => !q
        || (s.name || "").toLowerCase().includes(q)
        || (s.phone || "").includes(q))
      .slice(0, 30);
  }, [allStaff, nearbyIds, staffQ]);

  const submit = async () => {
    if (!selectedId || !date) {
      toast.error("Pick a staff and a date");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/service-requests/${sr.id}/schedule`, {
        assigned_staff_id: selectedId,
        scheduled_date: date,
        scheduled_slot: slot || null,
        assigned_vehicle_id: vehicleId || null,
        supervising_office_staff_id: supervisorId || null,
        auto_create_task: autoTask,
      });
      toast.success(autoTask ? "Scheduled and task queued" : "Scheduled");
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         onClick={onClose} data-testid="sr-assign-modal">
      <div className="bg-white rounded-lg border border-[#E5E2D9] w-full max-w-xl max-h-[92vh] flex flex-col"
           onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-[#E5E2D9]">
          <div className="text-[11px] uppercase tracking-[0.14em] text-[#2D5A27] font-semibold">
            Schedule request
          </div>
          <div className="font-heading text-lg mt-1">{sr.customer_name}</div>
          <div className="text-[11px] text-[#5C5A55]">
            {sr.request_no} · {sr.service_category}
            {sr.preferred_date && <> · asked for {sr.preferred_date}</>}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-4">
          {/* Date + Slot */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-[0.14em] text-[#5C5A55] font-semibold">
                Scheduled date
              </label>
              <input type="date" value={date} onChange={e => setDate(e.target.value)}
                     data-testid="sr-assign-date"
                     className="mt-1 w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.14em] text-[#5C5A55] font-semibold">
                Slot
              </label>
              <input type="text" value={slot} onChange={e => setSlot(e.target.value)}
                     placeholder="e.g. 9-11 AM"
                     data-testid="sr-assign-slot"
                     className="mt-1 w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
            </div>
          </div>

          {/* Assign staff — nearby first */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.14em] text-[#5C5A55] font-semibold">
              Assign staff · {allStaff.length} total
            </label>
            {sr.nearby_agents.length > 0 && (
              <div className="mt-1.5">
                <div className="text-[9px] uppercase tracking-[0.14em] text-[#2D5A27] font-bold mb-1">
                  Nearby ({sr.nearby_agents.length})
                </div>
                <div className="space-y-1.5">
                  {sr.nearby_agents.map(a => (
                    <label key={a.id}
                           data-testid={`sr-assign-opt-${a.id}`}
                           className={`flex items-center gap-2 px-3 py-2 rounded border cursor-pointer ${
                             selectedId === a.id
                               ? "border-[#2D5A27] bg-[#F0F7EE]"
                               : "border-[#E5E2D9] hover:bg-[#F7F6F2]"
                           }`}>
                      <input type="radio" name="agent" value={a.id}
                             checked={selectedId === a.id}
                             onChange={() => setSelectedId(a.id)}
                             className="accent-[#2D5A27]"/>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium">{a.name}</div>
                        <div className="text-[11px] text-[#5C5A55]">
                          {a.road_km} km road · {Math.round(a.eta_min)} min ETA
                          {a.route_source === "fallback" && <> · estimate</>}
                          {a.duty_status && <> · {a.duty_status}</>}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-3">
              <div className="flex items-center justify-between">
                <div className="text-[9px] uppercase tracking-[0.14em] text-[#8A8780] font-bold">
                  All staff
                </div>
                <input value={staffQ} onChange={e => setStaffQ(e.target.value)}
                       placeholder="Search by name or phone…"
                       data-testid="sr-assign-staff-search"
                       className="h-7 px-2 border border-[#E5E2D9] rounded text-[11px] w-52"/>
              </div>
              <div className="mt-1 max-h-40 overflow-y-auto border border-[#E5E2D9] rounded">
                {otherStaff.length === 0 && (
                  <div className="p-3 text-center text-xs text-[#8A8780] italic">
                    No other staff match this filter.
                  </div>
                )}
                {otherStaff.map(s => (
                  <label key={s.id}
                         data-testid={`sr-assign-staff-opt-${s.id}`}
                         className={`flex items-center gap-2 px-2 py-1.5 border-b border-[#F0ECE3] last:border-b-0 cursor-pointer ${
                           selectedId === s.id ? "bg-[#F0F7EE]" : "hover:bg-[#F7F6F2]"
                         }`}>
                    <input type="radio" name="agent" value={s.id}
                           checked={selectedId === s.id}
                           onChange={() => setSelectedId(s.id)}
                           className="accent-[#2D5A27]"/>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-medium truncate">{s.name}</div>
                      <div className="text-[10px] text-[#5C5A55]">
                        {s.phone} · {s.role || "field_staff"}
                        {s.status && <> · {s.status}</>}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Vehicle */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.14em] text-[#5C5A55] font-semibold">
              Assign vehicle · optional
            </label>
            <select value={vehicleId} onChange={e => setVehicleId(e.target.value)}
                    data-testid="sr-assign-vehicle"
                    className="mt-1 w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm bg-white">
              <option value="">— No vehicle —</option>
              {vehicles.map(v => {
                // Iter 201 · vehicles are stored with `vehicle_no` on this
                // tenant; older seeds also expose `registration_no`/`number`.
                // Show a friendly reg-no + type instead of the raw UUID.
                const reg = v.vehicle_no || v.registration_no || v.number;
                const typeLabel = v.type ? String(v.type).replace(/_/g, " ") : "";
                return (
                  <option key={v.id} value={v.id}>
                    {reg || `Vehicle ${v.id.slice(0, 6)}`}
                    {typeLabel ? ` · ${typeLabel}` : ""}
                    {v.capacity_kg ? ` · ${v.capacity_kg}kg` : ""}
                    {v.status && v.status !== "active" ? ` (${v.status})` : ""}
                  </option>
                );
              })}
            </select>
          </div>

          {/* Iter 220h — Supervising office staff */}
          <div>
            <label className="text-[10px] uppercase tracking-[0.14em] text-[#5C5A55] font-semibold">
              Supervising office staff · optional
            </label>
            <select value={supervisorId} onChange={e => setSupervisorId(e.target.value)}
                    data-testid="sr-assign-supervisor"
                    className="mt-1 w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm bg-white">
              <option value="">— None —</option>
              {officeUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name || u.email}
                  {u.role ? ` · ${u.role.replace(/_/g, " ")}` : ""}
                </option>
              ))}
            </select>
            {sr.responsible_office_staff_id && sr.responsible_office_staff_id === supervisorId && (
              <div className="mt-1 text-[10px] text-[#5C5A55]">
                Pre-filled from customer's Fixed Responsibility.
              </div>
            )}
          </div>

          {/* Auto-create task */}
          <label className="flex items-center gap-2 cursor-pointer"
                 data-testid="sr-assign-autotask">
            <input type="checkbox" checked={autoTask}
                   onChange={e => setAutoTask(e.target.checked)}
                   className="accent-[#2D5A27] w-4 h-4"/>
            <div>
              <div className="text-sm font-medium">Auto-create task in Operations</div>
              <div className="text-[11px] text-[#5C5A55]">
                Push a work item to the assigned staff&apos;s Daily Planning tab
                for {date || "the chosen date"}.
              </div>
            </div>
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-[#E5E2D9] bg-[#F7F6F2]">
          <button onClick={onClose} className="h-9 px-3 rounded border border-[#E5E2D9] text-sm">Cancel</button>
          <button onClick={submit} disabled={busy || !selectedId || !date}
                  data-testid="sr-assign-confirm"
                  className="h-9 px-4 rounded bg-[#2D5A27] hover:bg-[#3A7033] disabled:opacity-60 text-white text-sm font-medium">
            {busy ? "Scheduling…" : "Schedule"}
          </button>
        </div>
      </div>
    </div>
  );
}

function SrRescheduleModal({ sr, onClose, onDone }) {
  const [busy, setBusy] = useState(false);
  const [date, setDate] = useState(sr.scheduled_date || sr.preferred_date || "");
  const [slot, setSlot] = useState(sr.scheduled_slot || sr.preferred_slot || "");
  const [reason, setReason] = useState("");
  const submit = async () => {
    if (!date) { toast.error("Pick a new date"); return; }
    if (!reason.trim() || reason.trim().length < 3) {
      toast.error("Reason must be at least 3 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/service-requests/${sr.id}/reschedule`, {
        scheduled_date: date,
        scheduled_slot: slot || null,
        reason: reason.trim(),
      });
      toast.success("Rescheduled");
      onDone();
    } catch (e) { toast.error(formatApiError(e)); }
    setBusy(false);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         onClick={onClose} data-testid="sr-resch-modal">
      <div className="bg-white rounded-lg border border-[#E5E2D9] w-full max-w-md p-5 space-y-3"
           onClick={e => e.stopPropagation()}>
        <div>
          <div className="text-[11px] uppercase tracking-[0.14em] text-[#0B2B66] font-semibold">
            Re-schedule
          </div>
          <div className="font-heading text-lg mt-1">{sr.customer_name}</div>
          <div className="text-[11px] text-[#5C5A55]">{sr.request_no}</div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="text-[10px] uppercase tracking-[0.14em] text-[#5C5A55] font-semibold">New date</label>
            <input type="date" value={date} onChange={e => setDate(e.target.value)}
                   data-testid="sr-resch-date"
                   className="mt-1 w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.14em] text-[#5C5A55] font-semibold">Slot</label>
            <input type="text" value={slot} onChange={e => setSlot(e.target.value)}
                   placeholder="9-11 AM"
                   data-testid="sr-resch-slot"
                   className="mt-1 w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
          </div>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-[0.14em] text-[#5C5A55] font-semibold">Reason</label>
          <textarea value={reason} onChange={e => setReason(e.target.value)}
                    rows={2}
                    data-testid="sr-resch-reason"
                    placeholder="Why is this being rescheduled?"
                    className="mt-1 w-full px-2 py-1.5 border border-[#E5E2D9] rounded text-sm resize-none"/>
        </div>
        <div className="flex items-center justify-end gap-2">
          <button onClick={onClose} className="h-9 px-3 rounded border border-[#E5E2D9] text-sm">Cancel</button>
          <button onClick={submit} disabled={busy}
                  data-testid="sr-resch-confirm"
                  className="h-9 px-3 rounded bg-[#0B2B66] hover:bg-[#1F3A57] disabled:opacity-60 text-white text-sm font-medium">
            {busy ? "Saving…" : "Reschedule"}
          </button>
        </div>
      </div>
    </div>
  );
}
